import { MutationCache, QueryClient } from '@tanstack/react-query'
import { isProblem, keyAdvice, keyAnswer, problemDetail } from '@/shared/api'
import { classifyError, reportError } from '@/shared/lib/telemetry'
import { toast } from '@/shared/lib/toast'

/**
 * Повтор помогает только на сбое сети и на 5xx. Всё остальное повтором не чинится.
 *
 * ⚠️ Соблазнительное `if (!isProblem(error)) return true // сеть` — классификация методом исключения,
 * и под неё попадает наш собственный баг: TypeError из `select`, из queryFn, из перехватчика. Такой
 * баг был бы повторён дважды, замаскирован под сетевой сбой и добавил бы три секунды спиннера вместо
 * честной ошибки. Поэтому спрашиваем `classifyError` — он отличает сеть от бага приложения и от
 * устаревшего деплоя (там нужна перезагрузка, а не ретрай).
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false

  if (isProblem(error)) {
    // «Попробуй позже», а не «ты неправ».
    if (error.status === 408 || error.status === 429) return true
    // 401 сюда тоже попадает и не ретраится: перехватчик уже увёл на /login.
    return error.status >= 500
  }

  return classifyError(error) === 'network'
}

/**
 * Тост за экран, которого уже нет. Встаёт он поверх ДРУГОГО экрана, и общий диагноз говорил бы про
 * него («Здесь ничего нет»), поэтому речь всегда о предмете (`meta.subject`).
 *
 * Отказ сервера (4xx) — исход известен: заголовок — предмет, пояснение — слова сервера целиком,
 * его пояснение пишется в продолжение заголовка («Загрузка уже обработана. Она уже применена.»).
 * Кроме ответа ключа «занят» (`keyAnswer`): первый запрос ещё идёт или исход его неизвестен.
 *
 * ⚠️ Сбой сети и 5xx — исход НЕИЗВЕСТЕН: запрос мог дойти, а ответ потеряться. Предмет в заголовке
 * тоста-ошибки прочитался бы как «не сделано», а повтор записи платежа из нового окна — это новый
 * ключ идемпотентности, то есть второй платёж. Поэтому заголовок говорит о незнании.
 */
function lostScreenWords(
  error: unknown,
  subject: string,
  unknownOutcome = 'проверьте, прежде чем повторять.',
): { title: string; description: string } {
  // Ответ ключа «занят» или «исход неизвестен» — не отказ: он о первом запросе, и его совет —
  // держателю ключа, а ключ ушёл вместе с экраном.
  const pending = keyAnswer(error) === 'busy' || keyAnswer(error) === 'unknown'
  if (isProblem(error) && error.status >= 400 && error.status < 500 && !pending) {
    return { title: subject, description: serverWords(error) }
  }
  // Busy на 5xx — выполнено, а ответ не собран: заголовок сервера это и говорит, а его пояснение —
  // совет держателю ключа, а ключ ушёл вместе с экраном.
  if (isProblem(error) && error.status >= 500 && keyAnswer(error) === 'busy') {
    const head = /[.!?…]$/.test(error.title) ? error.title : `${error.title}.`
    return { title: subject, description: `${head} Проверьте, прежде чем повторять.` }
  }
  return { title: 'Не знаем, дошло ли', description: `${subject} — ${unknownOutcome}` }
}

/** Слова ответа сервера: заголовок, за ним пояснение. */
function serverWords(error: { title: string }): string {
  const detail = problemDetail(error)
  if (!detail) return error.title
  const head = /[.!?…]$/.test(error.title) ? error.title : `${error.title}.`
  return `${head} ${detail}`
}

export function createQueryClient(): QueryClient {
  // Смотрит на мутацию наблюдатель со слушателями, то есть смонтированный экран: прицепиться к ней
  // наблюдатель может и без них (`mutate` уже после ухода).
  const observers = new WeakMap<object, Set<{ hasListeners(): boolean }>>()

  // Телеметрию и тост вешаем на MutationCache, а не на defaultOptions.mutations.onError.
  //
  // TanStack Query собирает опции мутации неглубоким спредом, поэтому локальный
  // `useMutation({ onError })` — а он рано или поздно появится — ПОЛНОСТЬЮ заменит обработчик
  // из defaultOptions, и для этой мутации молча замолчат и телеметрия, и тосты. Обработчик на
  // MutationCache вызывается независимо от локального, и затереть его нельзя.
  //
  // План фазы 1 предписывает обратное (`defaultOptions.mutations.onError`) — не следовать ему.
  const mutationCache = new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      reportError(error, { source: 'mutation' })

      /*
       * Неудавшуюся мутацию человек обязан заметить: в отличие от запроса, она означает, что его
       * действие НЕ выполнено — или неизвестно, выполнено ли. Молчаливый провал — худшее тут.
       *
       * ⚠️ Кроме случая, когда отказ объясняет не тост. Тогда общий тост вредит: он говорит «Что-то
       * сломалось, обновите страницу» (409 для `classifyError` неотличим от бага приложения) поверх
       * точной плашки рядом, а на «идентификатор занят» это прямо противоположный совет —
       * перечитывание вернёт то же занятое имя.
       *
       * Молчит мутация, а не обработчик — по флагам `meta` (`shared/api/mutation-meta.ts`). Обратное
       * правило («не тостить 409») спрятало бы отказ там, где его не показывает никто. Для
       * `handledInPlace` решение отложено до доставки, ниже. Телеметрия уходит в любом случае.
       */
      const meta = mutation.meta
      // `catch` вызывающего сообщит сам, и без экрана тоже. Про экран — при доставке, ниже.
      if (meta?.reportedByCaller === true || meta?.handledInPlace === true) return

      // Ответ самого ключа пишет менеджеру, что делать, — общий диагноз («обновите страницу») его
      // исказил бы: перезагрузка теряет ключ формы.
      const advice = keyAdvice(error)
      if (isProblem(error) && advice) {
        toast.error(error, { title: error.title, description: problemDetail(error) ?? advice })
        return
      }
      toast.error(error)
    },
  })

  mutationCache.subscribe((event) => {
    if (event.type === 'observerAdded' || event.type === 'observerRemoved') {
      const seen = observers.get(event.mutation) ?? new Set()
      if (event.type === 'observerAdded') seen.add(event.observer)
      else seen.delete(event.observer)
      observers.set(event.mutation, seen)
      return
    }
    /*
     * ⚠️ «Покажу на экране» держится, только пока экран ЕСТЬ, а ответ приходит и после ухода:
     * навигация при открытом окне, листание броней, другая страница. Решаем В МОМЕНТ ДОСТАВКИ:
     * наблюдатели получают отказ в том же `batch` строкой раньше (`Mutation#dispatch`). Общий
     * `onError` для этого рано — он идёт до ожидания локальных `onError`/`onSettled`, и уход экрана
     * в это ожидание потерял бы отказ.
     */
    if (event.type !== 'updated' || event.action.type !== 'error') return
    const meta = event.mutation.meta
    if (meta?.handledInPlace !== true) return
    const watched = [...(observers.get(event.mutation) ?? [])].some((o) => o.hasListeners())
    if (!watched) {
      const words = lostScreenWords(event.action.error, meta.subject, meta.unknownOutcome)
      toast.error(event.action.error, words)
    }
  })

  return new QueryClient({
    mutationCache,
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
        refetchOnWindowFocus: true,
        // Поллинг не должен молотить в фоновой вкладке.
        refetchIntervalInBackground: false,
        staleTime: 30_000,
        gcTime: 5 * 60_000,
      },
      mutations: {
        // Мутации не ретраим: повтор может задвоить действие — например, создать вторую бронь.
        retry: false,
      },
    },
  })
}
